import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { normalizeJsonApi } from '../../utils/entity-states/json-api-normalizer';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getData(resource: string, customParams?: Record<string, any>): Observable<any> {
    let params = new HttpParams();

    if (customParams) {
      for (const key in customParams) {
        const value = customParams[key];
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          for (const subKey in value) {
            if (Object.prototype.hasOwnProperty.call(value, subKey)) {
              params = params.set(`${key}[${subKey}]`, value[subKey].toString());
            }
          }
        }
        else {
          params = params.set(key, value.toString());
        }
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get<any>(`${this.baseUrl}/${resource}`, { params }).pipe(
      map(response => normalizeJsonApi(response))
    );
  }

  getDataSingle(
    resource: string,
    id: string,
    customParams?: Record<string, string | number>
  ) {
    let params = new HttpParams();

    if (customParams) {
      for (const key in customParams) {
        params = params.set(key, customParams[key].toString());
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.get<any>(`${this.baseUrl}/${resource}/${id}`, { params })
      .pipe(map(response => normalizeJsonApi(response)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createDataSingle(resource: string, data: any) {
    const url = `${this.baseUrl}/${resource}`;

    // Detect relationships automatically
    let params = new HttpParams();
    if (data?.data?.relationships) {
      const include = Object.keys(data.data.relationships).join(',');
      params = params.set('include', include);
    }

    // Set JSON:API Content-Type header
    const headers = new HttpHeaders({
      'Content-Type': 'application/vnd.api+json'
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.post<any>(url, data, { headers, params }).pipe(
      map(response => normalizeJsonApi(response))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateDataSingle(resource: string, id: string, data: any) {
    const url = `${this.baseUrl}/${resource}/${id}`;

    // Detect relationships to automatically include
    let params = new HttpParams();
    if (data?.data?.relationships) {
      const include = Object.keys(data.data.relationships).join(','); // keep camelCase
      params = params.set('include', include);
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/vnd.api+json'
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.http.patch<any>(url, data, { headers, params }).pipe(
      map(response => normalizeJsonApi(response))
    );
  }

  deleteDataSingle(resource: string, id: string) {
    const url = `${this.baseUrl}/${resource}/${id}`;
    const headers = new HttpHeaders({
      Accept: 'application/vnd.api+json'
    });

    return this.http.delete<void>(url, { headers });
  }
}
